import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { operatorService } from '../../services/api';
import { Users, FileText, Upload, Loader2 } from 'lucide-react';

/**
 * Dashboard per operatori
 */
function OperatorDashboard() {
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      const response = await operatorService.getMyClients();
      setClients(response.data?.clients || []);
    } catch (error) {
      console.error('Errore caricamento clienti:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-primary" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-foreground">Dashboard Operatore</h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Gestisci i tuoi clienti assegnati
        </p>
      </div>

      {/* Riepilogo */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
        <div className="bg-card rounded-lg border border-border p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2.5 sm:p-3 rounded-lg text-blue-600 bg-blue-100 dark:bg-blue-900 flex-shrink-0">
              <Users size={20} className="sm:w-6 sm:h-6" />
            </div>
            <div>
              <p className="text-xl sm:text-2xl font-bold text-foreground">{clients.length}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">Clienti assegnati</p>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2.5 sm:p-3 rounded-lg text-green-600 bg-green-100 dark:bg-green-900 flex-shrink-0">
              <FileText size={20} className="sm:w-6 sm:h-6" />
            </div>
            <div>
              <Link
                to="/documents"
                className="text-xl sm:text-2xl font-bold text-foreground hover:text-primary active:text-primary touch-manipulation"
              >
                Documenti →
              </Link>
              <p className="text-xs sm:text-sm text-muted-foreground">Gestisci documenti</p>
            </div>
          </div>
        </div>
      </div>

      {/* Lista clienti */}
      <div className="bg-card rounded-lg border border-border">
        <div className="p-3 sm:p-4 border-b border-border flex items-center justify-between">
          <h2 className="text-base sm:text-lg font-semibold">I miei clienti</h2>
          <Link
            to="/my-clients"
            className="text-sm text-primary hover:underline active:underline py-1 touch-manipulation"
          >
            Vedi tutti
          </Link>
        </div>

        {clients.length === 0 ? (
          <div className="p-6 sm:p-8 text-center">
            <Users className="mx-auto text-muted-foreground mb-4" size={40} />
            <p className="text-muted-foreground text-sm sm:text-base">
              Nessun cliente assegnato al momento.
            </p>
            <p className="text-xs sm:text-sm text-muted-foreground mt-2">
              L'amministratore ti assegnerà i clienti da gestire.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {clients.slice(0, 5).map((client) => (
              <div
                key={client._id}
                className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-accent/50 active:bg-accent/50"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary font-medium text-sm">
                      {client.firstName?.[0]}
                      {client.lastName?.[0]}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm sm:text-base truncate">
                      {client.firstName} {client.lastName}
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground truncate">
                      Ospite: {client.guestName || 'Non specificato'}
                      {client.guestRoom && ` - Stanza ${client.guestRoom}`}
                    </p>
                  </div>
                </div>
                <Link
                  to={`/documents?clientId=${client._id}`}
                  className="flex items-center justify-center gap-2 px-4 py-2 text-sm bg-primary/10 text-primary rounded-lg hover:bg-primary/20 active:bg-primary/20 w-full sm:w-auto min-h-[44px] touch-manipulation"
                >
                  <Upload size={16} />
                  Documenti
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default OperatorDashboard;
